import React from "react";
import "./Service_pages.scss";
import img from "./services/tooth_extraction.png";
import dentalImage from "./services/dental.png";
import { FaCheckCircle, FaTooth, FaSyringe, FaSmile, FaUserMd, FaLaptopMedical, FaClinicMedical, FaHeart, FaRupeeSign, FaMapMarkerAlt } from 'react-icons/fa';
import { MdOutlineHealthAndSafety, MdOutlineStarPurple500 } from "react-icons/md";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";

const ToothExtraction = () => {

  const types = [
    {
      title: "Simple Extraction",
      icon: <FaTooth />,
      desc: "Ideal for teeth visible above the gum line. A quick, routine procedure performed under local anesthesia to ensure a pain-free experience.",
    },
    {
      title: "Surgical Extraction",
      icon: <MdOutlineStarPurple500 />,
      desc: "Required for teeth broken at the gum line or not fully erupted. Our surgeons use precise techniques to ensure minimal tissue trauma and faster healing.",
    },
    {
      title: "Impacted Wisdom Teeth",
      icon: <MdOutlineHealthAndSafety />,
      desc: "Specialized removal of third molars that are stuck or growing at an angle. We use advanced 3D imaging to map the nerves for a safe surgical outcome.",
    },
  ];

  const disadvantages = [
    {
      title: "Immediate Pain Relief",
      description: "Instantly removes the source of severe toothache, infection, or pressure caused by crowding.",
    },
    {
      title: "Prevents Spread of Infection",
      description: "Extracting a non-restorable infected tooth prevents bacteria from affecting the jawbone and neighboring healthy teeth.",
    },
    {
      title: "Safe & Sterile Environment",
      description: "At Kosmix, we follow international sterilization protocols to ensure 100% safety during every surgical procedure.",
    },
    {
      title: "Modern Anesthesia",
      description: "We use advanced numbing techniques so you feel zero pain during the procedure, making it a stress-free experience.",
    },
    {
      title: "Comprehensive Aftercare",
      description: "Every patient receives a detailed recovery plan and follow-up support to ensure smooth healing without complications like dry socket.",
    },
  ];

  const reasons = [
    {
      icon: <FaUserMd />,
      title: 'Expert Oral Surgeons',
      description: 'Our specialists have 10+ years of experience in complex dental extractions.'
    },
    {
      icon: <FaLaptopMedical />,
      title: 'Advanced Diagnostics',
      description: 'Digital X-rays and AI-powered planning for maximum surgical precision.'
    },
    {
      icon: <FaClinicMedical />,
      title: 'Modern, Hygienic Facility',
      description: 'A clean, calm, and fully equipped clinic designed for patient comfort.'
    },
    {
      icon: <FaHeart />,
      title: 'Compassionate Care',
      description: 'We prioritize patient comfort and dental anxiety management at every step.'
    },
    {
      icon: <FaRupeeSign />,
      title: 'Transparent Pricing',
      description: 'Affordable treatment options with clear costs and zero hidden charges.'
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Prime HSR Layout Location',
      description: 'Conveniently located for residents of HSR, BTM, and Sarjapur Road.'
    },
    {
      icon: <FaSmile />,
      title: '1000+ Pain-Free Procedures',
      description: 'Trusted by the community for safe and gentle dental extractions.'
    }
  ];

  return (
    <>
      <div className="service1-section">
        <Helmet>
          <title>Tooth Extraction in HSR Layout | Pain-Free Dental Removal</title>
          <meta
            name="description"
            content="Safe, pain-free tooth extraction in HSR Layout at Kosmix Dental Clinic. Expert care for decayed, damaged, or impacted wisdom teeth."
          />
          <link
            rel="canonical"
            href="https://www.kosmixdentalclinic.com/services/tooth-extraction-in-hsr-layout"
          />
        </Helmet>

        {/* HERO SECTION */}
        <div className="hero">
          <h1 className="service1-title-text" data-aos="fade-down" data-aos-delay="200" style={{ fontFamily: 'initial' }}>
            Tooth Extraction in HSR Layout
          </h1>

          <div className="hero-content">
            <div className="hero-image" data-aos="zoom-in-right" data-aos-delay="200" data-aos-duration="800">
              <img src={img} alt="Tooth Extraction Treatment" />
            </div>

            <div className="hero-text" data-aos="zoom-in-left" data-aos-delay="200" data-aos-duration="800">
              <p>
                At <strong>Kosmix Dental Clinic</strong>, we prioritize saving your natural teeth. However, when a tooth is beyond repair due to decay, trauma, or crowding, a professional extraction is the safest way to restore your oral health and prevent further complications.
              </p>
              <p>
                Led by our team of expert oral surgeons, we specialize in providing a completely pain-free experience. Using modern technology and gentle techniques, we ensure that even complex surgical extractions are handled with precision and care.
              </p>
              <p>
                From routine simple extractions to surgical wisdom tooth removal, our approach focuses on your comfort, rapid healing, and long-term dental wellness. We walk you through every step to ensure you feel confident and relaxed.
              </p>
            </div>
          </div>
        </div>

        {/* DEFINITION SECTION */}
        <div className="extraction-definition-card" data-aos="fade-up" data-aos-delay="300">
          <SectionTitle title="What is Tooth Extraction?" />
          <p className="definition-intro">
            Tooth extraction is the careful removal of a tooth from its socket in the jawbone. It is a vital procedure when a tooth poses a threat to your overall dental health.
          </p>
          <div className="extraction-types-flex">
            <div className="type-item" data-aos="fade-right" data-aos-delay="400">
              <div className="type-icon-wrapper"><FaTooth /></div>
              <h3>Simple Extraction</h3>
              <p>Performed on teeth visible above the gum line, usually requiring only local anesthesia.</p>
            </div>
            <div className="type-item" data-aos="fade-left" data-aos-delay="500">
              <div className="type-icon-wrapper"><FaSyringe /></div>
              <h3>Surgical Extraction</h3>
              <p>Required for teeth that are impacted or broken below the gums, involving a more detailed clinical approach.</p>
            </div>
          </div>
        </div>

        {/* WHY YOU NEED IT SECTION */}
        <section className="importance-section">
          <div className="content">
            <h2 className="section-title" data-aos="fade-up">Why is Tooth Extraction Necessary?</h2>
            <p className="text_first" data-aos="fade-up" data-aos-delay="100">
              An extraction is recommended when other restorative treatments like fillings or root canals cannot save the tooth:
            </p>
          </div>

          <div className="importance-cards-container">
            {[
              { title: "Severe infection or dental abscess" },
              { title: "Advanced gum disease (Periodontitis)" },
              { title: "Impacted or painful wisdom teeth" },
              { title: "Preparation for orthodontic treatment" },
              { title: "Fractured or broken teeth beyond repair" },
              { title: "Removing milk teeth to allow permanent growth" },
            ].map((item, index) => (
              <div className="importance-card1" data-aos="fade-up" data-aos-delay={150 + index * 100} key={index}>
                <FaCheckCircle className="card-icon" />
                <h4>{item.title}</h4>
              </div>
            ))}
          </div>
        </section>

        {/* MIDDLE HIGHLIGHT SECTION */}
        <section className="why-choose-us">
          <div className="wrapper">
            <div className="image-side" data-aos="zoom-in" data-aos-delay="100">
              <img src={dentalImage} alt="Pain-Free Extraction Care" />
            </div>

            <div className="text-side" data-aos="fade-up" data-aos-delay="300">
              <h2>Safe & Comfortable Dental Extractions at Kosmix</h2>
              <p style={{ color: 'black' }}>
                Experiencing severe tooth pain? At <strong>Kosmix Dental Clinic</strong>, HSR Layout, we offer specialized extraction services tailored to your needs. Our surgeons utilize the latest diagnostic tools to ensure every procedure is mapped out for maximum safety.
              </p>
              <p style={{ color: 'black' }}>
                We understand that the thought of an extraction can be stressful. That’s why we provide a calm environment and use modern anesthesia that eliminates pain, ensuring you feel at ease from start to finish.
              </p>
              <p style={{ color: 'black' }}>
                Whether it's a routine removal or a complex wisdom tooth surgery, we focus on preserving your bone health and preparing you for future replacement options like implants if needed.
              </p>
              <p style={{ color: 'black' }}>
                <strong>Book your consultation today</strong> and let our experts help you achieve a pain-free, healthy smile in a modern clinical setting.
              </p>
            </div>
          </div>
        </section>

        {/* TYPES SECTION */}
        <section className="crown-types-section">
          <div className="content" data-aos="fade-up">
            <SectionTitle title="Types of Extraction Services We Provide" />
          </div>

          <div className="crown-grid">
            {types.map((item, idx) => (
              <div className="crown-card" key={idx} data-aos="zoom-in" data-aos-delay={idx * 200}>
                <div className="icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* STEP-BY-STEP PROCESS */}
        <section className="timeline-container" style={{ overflowX: 'hidden', padding: '0 1rem' }}>
          <div className="content" data-aos="fade-up">
            <SectionTitle title="The Tooth Extraction Journey" />
            <p className="text_first">
              Our streamlined clinical process ensures safety and rapid recovery:
            </p>
          </div>

          <div className="timeline-step" data-aos="fade-right">
            <div className="timeline-icon">🔍</div>
            <div className="timeline-content">
              <h3>Clinical Examination</h3>
              <p>We use digital X-rays to assess the tooth's root position and the density of the surrounding bone.</p>
            </div>
            <div className="timeline-icon">1</div>
          </div>

          <div className="timeline-step" data-aos="fade-left">
            <div className="timeline-icon">💉</div>
            <div className="timeline-content">
              <h3>Numbing & Comfort</h3>
              <p>Localized anesthesia is applied to ensure the area is completely numb, guaranteeing a pain-free procedure.</p>
            </div>
            <div className="timeline-icon">2</div>
          </div>

          <div className="timeline-step" data-aos="fade-right">
            <div className="timeline-icon">🦷</div>
            <div className="timeline-content">
              <h3>Careful Removal</h3>
              <p>The tooth is gently loosened and removed using precision tools to protect the surrounding gum and bone.</p>
            </div>
            <div className="timeline-icon">3</div>
          </div>

          <div className="timeline-step" data-aos="fade-left">
            <div className="timeline-icon">✨</div>
            <div className="timeline-content">
              <h3>Site Disinfection</h3>
              <p>The area is thoroughly cleaned and sanitized to prevent infection, and gauze is applied to start the healing.</p>
            </div>
            <div className="timeline-icon">4</div>
          </div>

          <div className="timeline-step" data-aos="fade-right">
            <div className="timeline-icon">📝</div>
            <div className="timeline-content">
              <h3>Recovery Guidance</h3>
              <p>We provide a comprehensive aftercare kit and instructions on diet and oral hygiene to ensure fast healing.</p>
            </div>
            <div className="timeline-icon">5</div>
          </div>
        </section>

        {/* BENEFITS SECTION (REPLACES DISADVANTAGES) */}
        <section className="advantages-section">
          <div className="content" data-aos="fade-up">
            <SectionTitle title="Benefits of Professional Care at Kosmix" />
          </div>

          <div className="advantages-grid">
            {disadvantages.map((item, index) => (
              <div key={index} className="advantage-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <FaCheckCircle className="advantage-icon" />
                <div>
                  <h3>{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* WHY CHOOSE US REDESIGN */}
        <section className="advantage-hub-section">
          <div className="container">
            <div className="hub-header" data-aos="fade-up">
              <span className="subtitle">The Kosmix Advantage</span>
              <SectionTitle title="Why Patients Choose Our HSR Layout Clinic" />
              <p className="description">
                We combine surgical excellence with a patient-first approach to provide the most comfortable dental experience in Bangalore.
              </p>
            </div>

            <div className="advantage-grid">
              {reasons.map((reason, index) => (
                <motion.div
                  key={index}
                  className="advantage-item"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <div className="icon-sphere">
                    {reason.icon}
                    <div className="pulse"></div>
                  </div>
                  <div className="content-box">
                    <h3>{reason.title}</h3>
                    <p>{reason.description}</p>
                  </div>
                  <div className="line-connector"></div>
                </motion.div>
              ))}
            </div>

            <p className="importance-footer" data-aos="fade-up" data-aos-delay="100">
              Don't let dental pain compromise your quality of life. Whether you need a simple extraction or complex wisdom tooth removal, Kosmix Dental Clinic in HSR Layout provides safe, expert, and compassionate care.
            </p>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="cta-section" data-aos='zoom-in'>
          <div className="cta-content">
            <h2>
              Schedule Your Appointment <span>Today!</span>
            </h2>
            <p>
              Experience the best pain-free tooth extraction treatment in HSR Layout with our expert surgeons.
            </p>
            <div className="cta-buttons">
              <Link to="/#appointment" >
                <button className="btn-primary">Book Appointment</button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

const SectionTitle = ({ title }) => (
  <h2 className="section-title">{title}</h2>
);

export default ToothExtraction;